import { FC, useState } from 'react';
import { CustomPopover } from 'components';
import { PaymentMethodKeyNameLookup, PopoverArrowTypes } from 'constants/common';
import { getPaymentMethodIconComponent } from 'containers/receipts/util';

interface PaymentMethodsCellProps {
  paymentMethodsList: string[];
  totalPayments: number;
}

const PaymentMethodsCell: FC<PaymentMethodsCellProps> = ({ paymentMethodsList, totalPayments }) => {
  const [showPopover, setShowPopover] = useState(false);

  const getPaymentMethodInfo = (paymentMethod: string) => {
    return {
      icon: getPaymentMethodIconComponent(paymentMethod),
      name: PaymentMethodKeyNameLookup[paymentMethod]
    };
  };

  if (!paymentMethodsList || paymentMethodsList.length === 0 || paymentMethodsList.every(method => method === '')) {
    return <div className="text-sbase text-primaryText">-</div>;
  }

  const visibleCount = 3;
  const displayMethods = paymentMethodsList.slice(0, visibleCount);
  const remainingCount = paymentMethodsList.length - visibleCount;
  const allMethods = paymentMethodsList.map(method => ({
    method,
    ...getPaymentMethodInfo(method)
  }));

  const handleClosePopover = () => {
    setShowPopover(false);
  };

  const handleTogglePopover = () => {
    setShowPopover(prev => !prev);
  };

  const renderIcon = (IconComponent: string | React.FunctionComponent, key: string) => {
    return (
      <span key={key} className="flex items-center">
        <IconComponent />
      </span>
    );
  };

  const renderPaymentMethodsList = (methods: string[]) => {
    return methods.map((method, index) => {
      const { icon, name } = getPaymentMethodInfo(method);
      return (
        <div key={index} className="flex shrink-0 items-center gap-1">
          {renderIcon(icon, `${method}-${index}`)}
          <span className="text-sm text-primaryText">
            {name}
            {index < methods.length - 1 ? ',' : ''}
          </span>
        </div>
      );
    });
  };

  if (remainingCount <= 0) {
    return <div className="flex flex-wrap items-center gap-1">{renderPaymentMethodsList(displayMethods)}</div>;
  }

  return (
    <div className="flex flex-wrap items-center gap-1">
      {renderPaymentMethodsList(displayMethods)}
      <CustomPopover
        show={showPopover}
        offset="left-1/2 transform -translate-x-1/2"
        showArrow={true}
        arrowType={PopoverArrowTypes.TOP}
        arrowOffset="left-1/2 transform -translate-x-1/2"
        onTogglePopover={handleTogglePopover}
        onClosePopover={handleClosePopover}
        padding="p-0"
        zIndex="z-10"
        anchorComponent={() => <div className="cursor-pointer text-sbase text-secondary">+{remainingCount}</div>}>
        <div className="flex flex-col gap-2 p-3" onClick={e => e.stopPropagation()}>
          <div className="mb-3 text-sm font-semibold text-accentText">
            {allMethods.length} Payment method{allMethods.length !== 1 ? 's' : ''} (Across {totalPayments} payment
            {totalPayments !== 1 ? 's' : ''})
          </div>
          <div className="flex flex-col gap-2">
            {allMethods.map(({ method, icon, name }, index) => (
              <div key={index} className="flex items-center gap-2">
                {renderIcon(icon, `${method}-popover-${index}`)}
                <span className="text-sm text-primaryText">{name}</span>
              </div>
            ))}
          </div>
        </div>
      </CustomPopover>
    </div>
  );
};

export default PaymentMethodsCell;
